import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { apiClient } from "../lib/api";
import Header from "../components/Header";
import SaveProgressModal from "../components/SaveProgressModal";
import StepUpload from "../components/wizard/StepUpload";
import StepReview from "../components/wizard/StepReview";
import StepResidency from "../components/wizard/StepResidency";
import StepHomeProfile from "../components/wizard/StepHomeProfile";
import StepSolarStatus from "../components/wizard/StepSolarStatus";
import StepFuturePlans from "../components/wizard/StepFuturePlans";
import StepResults from "../components/wizard/StepResults";
import StepPathChoice from "../components/wizard/StepPathChoice";
import StepEcoFlowPicker from "../components/wizard/StepEcoFlowPicker";
import StepLeadCapture from "../components/wizard/StepLeadCapture";
import StepThankYou from "../components/wizard/StepThankYou";
import {
  trackLead,
  trackWizardComplete,
  trackEcoFlowInterest,
  trackHotTOUCandidate,
} from "../lib/pixel";

const OWNER_STEPS = [
  { id: "upload", label: "Upload Bill" },
  { id: "review", label: "Confirm Data" },
  { id: "residency", label: "Owner / Renter" },
  { id: "home", label: "Your Home" },
  { id: "solar", label: "Existing Solar" },
  { id: "future", label: "Future Plans" },
  { id: "results", label: "Your Plan" },
  { id: "path", label: "Pick Your Path" },
  { id: "ecoflow", label: "Backup Power" },
  { id: "lead", label: "Connect" },
  { id: "done", label: "Complete" },
];

const RENTER_STEPS = [
  { id: "upload", label: "Upload Bill" },
  { id: "review", label: "Confirm Data" },
  { id: "residency", label: "Owner / Renter" },
  { id: "home", label: "Your Home" },
  { id: "results", label: "Your Tips" },
  { id: "path", label: "Pick Your Path" },
  { id: "ecoflow", label: "Backup Power" },
  { id: "lead", label: "Connect" },
  { id: "done", label: "Complete" },
];

const DEFAULT_HOME = {
  sqft: 1800,
  occupants: 3,
  ac_type: "central",
  water_heater: "electric",
  has_pool: false,
  has_ev: false,
  mini_split_count: 0,
};
const DEFAULT_SOLAR = {
  has_existing_solar: false,
  existing_solar_kw: 0,
  has_battery: false,
  roof_orientation: "south",
};
const DEFAULT_FUTURE = {
  plans_ev: false,
  plans_pool: false,
  plans_adu: false,
  plans_electrification: false,
};

export default function Wizard() {
  const navigate = useNavigate();
  const [residency, setResidency] = useState(null); // null until selected
  const [stepId, setStepId] = useState("upload");
  const [billData, setBillData] = useState(null);
  const [homeProfile, setHomeProfile] = useState(DEFAULT_HOME);
  const [solarStatus, setSolarStatus] = useState(DEFAULT_SOLAR);
  const [futurePlans, setFuturePlans] = useState(DEFAULT_FUTURE);
  const [analysis, setAnalysis] = useState(null);
  const [leadResult, setLeadResult] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [selectedEcoFlow, setSelectedEcoFlow] = useState(null);

  // On mount, check for resume state in sessionStorage
  useEffect(() => {
    const raw = sessionStorage.getItem("hipower_resume_state");
    const stepRaw = sessionStorage.getItem("hipower_resume_step");
    if (raw) {
      try {
        const s = JSON.parse(raw);
        if (s.billData) setBillData(s.billData);
        if (s.homeProfile) setHomeProfile(s.homeProfile);
        if (s.solarStatus) setSolarStatus(s.solarStatus);
        if (s.futurePlans) setFuturePlans(s.futurePlans);
        if (s.residency) setResidency(s.residency);
        if (s.analysis) setAnalysis(s.analysis);
        if (stepRaw) setStepId(stepRaw);
        toast.success("Restored your saved analysis");
      } catch (e) {
        console.error("Resume parse failed", e);
      } finally {
        sessionStorage.removeItem("hipower_resume_state");
        sessionStorage.removeItem("hipower_resume_step");
      }
    }
  }, []);

  const STEPS = residency === "renter" ? RENTER_STEPS : OWNER_STEPS;
  const stepIndex = Math.max(
    0,
    STEPS.findIndex((s) => s.id === stepId),
  );
  const progress = ((stepIndex + 1) / STEPS.length) * 100;

  const goTo = (id) => setStepId(id);
  const next = () => {
    const i = STEPS.findIndex((s) => s.id === stepId);
    if (i < STEPS.length - 1) setStepId(STEPS[i + 1].id);
  };
  const prev = () => {
    const i = STEPS.findIndex((s) => s.id === stepId);
    if (i > 0) setStepId(STEPS[i - 1].id);
  };

  const onResidencySelect = (val) => {
    setResidency(val);
    // jump to next step in the new path
    const newSteps = val === "renter" ? RENTER_STEPS : OWNER_STEPS;
    const i = newSteps.findIndex((s) => s.id === "residency");
    setStepId(newSteps[i + 1].id);
  };

  const runAnalysis = async () => {
    try {
      const payload = {
        monthly_kwh: billData.monthly_kwh,
        utility_provider: billData.utility_provider,
        island: billData.island || "Oahu",
        home_profile: homeProfile,
        future_plans: residency === "renter" ? DEFAULT_FUTURE : futurePlans,
        solar_status: residency === "renter" ? DEFAULT_SOLAR : solarStatus,
        residency_type: residency || "owner",
      };
      const { data } = await apiClient.post("/analysis/calculate", payload);
      setAnalysis(data);
      goTo("results");
    } catch (e) {
      toast.error("Could not compute analysis. Please check your inputs and try again.");
      console.error(e);
    }
  };

  const submitLead = async (form) => {
    try {
      const payload = {
        ...form,
        island: billData?.island,
        utility_provider: billData?.utility_provider,
        monthly_kwh: billData?.monthly_kwh,
        analysis_id: analysis?.analysis_id,
        current_monthly_bill: analysis?.recommendation?.current_monthly_bill,
        estimated_system_kw: residency === "owner" ? analysis?.recommendation?.system_kw : null,
        estimated_battery_kwh: residency === "owner" ? analysis?.recommendation?.battery_kwh : null,
        estimated_system_cost: residency === "owner" ? analysis?.recommendation?.net_cost : null,
        estimated_monthly_savings: analysis?.recommendation?.estimated_monthly_savings,
        estimated_25yr_savings: residency === "owner" ? analysis?.recommendation?.twenty_five_year_savings : null,
        energy_score: analysis?.energy_score?.score,
        residency_type: residency || "owner",
        tou_monthly_savings: analysis?.tou_savings?.monthly_savings,
        hot_tou_shift_candidate: !!analysis?.hot_tou_shift_candidate,
      };
      const { data } = await apiClient.post("/leads", payload);
      // Pixel: Lead conversion (Facebook ads will optimize toward this)
      trackLead(payload);
      if (payload.hot_tou_shift_candidate) trackHotTOUCandidate();
      trackWizardComplete();
      setLeadResult(data);
      goTo("done");
      toast.success("Thanks! A specialist will reach out within 24 hours.");
    } catch (e) {
      toast.error("Submission failed. Please try again.");
      console.error(e);
    }
  };

  const getResumeState = () => ({
    billData,
    homeProfile,
    solarStatus,
    futurePlans,
    residency,
    analysis,
  });

  const canSave = !!billData; // can only save after at least the bill is uploaded

  return (
    <div className="min-h-screen bg-slate-50">
      <Header variant="default" />

      <div className="pt-20">
        <div className="bg-white border-b border-slate-200 sticky top-16 z-40">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center gap-3 mb-3">
              {stepIndex > 0 && stepId !== "done" ? (
                <button
                  data-testid="wizard-back-btn"
                  onClick={prev}
                  className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm font-medium"
                >
                  <ArrowLeft size={16} /> Back
                </button>
              ) : (
                <button
                  data-testid="wizard-home-btn"
                  onClick={() => navigate("/")}
                  className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm font-medium"
                >
                  <ArrowLeft size={16} /> Home
                </button>
              )}
              <span className="text-sm font-semibold text-slate-700 ml-auto" data-testid="wizard-step-label">
                Step {stepIndex + 1} of {STEPS.length} &middot; {STEPS[stepIndex]?.label}
              </span>
              {canSave && stepId !== "done" && (
                <button
                  data-testid="wizard-save-btn"
                  onClick={() => setSaveOpen(true)}
                  className="ml-3 text-slate-500 hover:text-slate-900 flex items-center gap-1 text-xs font-semibold border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50"
                >
                  <FloppyDisk size={14} weight="duotone" /> Save & resume later
                </button>
              )}
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-sky-500 to-amber-500"
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              />
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-12 md:py-16">
          <AnimatePresence mode="wait">
            <motion.div
              key={stepId}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {stepId === "upload" && (
                <StepUpload onParsed={(data) => { setBillData(data); next(); }} />
              )}
              {stepId === "review" && billData && (
                <StepReview data={billData} onChange={setBillData} onContinue={next} />
              )}
              {stepId === "residency" && (
                <StepResidency value={residency} onSelect={onResidencySelect} />
              )}
              {stepId === "home" && (
                <StepHomeProfile
                  value={homeProfile}
                  onChange={setHomeProfile}
                  onContinue={residency === "renter" ? runAnalysis : next}
                />
              )}
              {stepId === "solar" && (
                <StepSolarStatus value={solarStatus} onChange={setSolarStatus} onContinue={next} />
              )}
              {stepId === "future" && (
                <StepFuturePlans value={futurePlans} onChange={setFuturePlans} onContinue={runAnalysis} />
              )}
              {stepId === "results" && analysis && (
                <StepResults
                  analysis={analysis}
                  billData={billData}
                  residency={residency || "owner"}
                  onContinue={next}
                />
              )}
              {stepId === "path" && (
                <StepPathChoice
                  onChoose={(choice) => {
                    if (choice === "assessment") {
                      // skip ecoflow step
                      const i = STEPS.findIndex((s) => s.id === "path");
                      const leadIdx = STEPS.findIndex((s) => s.id === "lead");
                      if (leadIdx > i) setStepId("lead");
                    } else {
                      goTo("ecoflow");
                    }
                  }}
                />
              )}
              {stepId === "ecoflow" && (
                <StepEcoFlowPicker
                  monthlyKwh={billData?.monthly_kwh || 0}
                  onProductSelect={(p) => { setSelectedEcoFlow(p); trackEcoFlowInterest(p); }}
                  selectedProductId={selectedEcoFlow?.id}
                  onContinueToLead={() => goTo("lead")}
                />
              )}
              {stepId === "lead" && (
                <StepLeadCapture
                  defaults={{
                    street: billData?.service_address_street || "",
                    city: billData?.service_address_city || "",
                    zip: billData?.service_address_zip || "",
                    full_name: billData?.customer_name || "",
                  }}
                  residency={residency || "owner"}
                  selectedEcoFlow={selectedEcoFlow}
                  onSubmit={submitLead}
                />
              )}
              {stepId === "done" && (
                <StepThankYou leadResult={leadResult} analysis={analysis} residency={residency || "owner"} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <SaveProgressModal
        open={saveOpen}
        onOpenChange={setSaveOpen}
        getState={getResumeState}
        currentStep={stepId}
      />
    </div>
  );
}
